import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi, ResponseError } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { invalidateAndRefetchAppointmentBookings } from "@/features/appointments/utils/appointment-bookings-query";
import { invalidatePostedTuitionLedgerQueries } from "@/features/tuition/hooks/invalidate-posted-tuition-ledger-queries";

export const useAppointmentBookingActions = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate: cancelBooking, isPending: isCanceling } = useMutation({
    mutationFn: async (bookingId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );
      return service.apiAppointmentsBookingIdCancelPut({
        id: bookingId,
      });
    },
    onSuccess: async () => {
      await invalidateAndRefetchAppointmentBookings(queryClient);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Appointments],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.AppointmentSchedule],
      });
      await invalidatePostedTuitionLedgerQueries(queryClient);
      toast({
        title: "Booking canceled",
        description: "Private lesson booking has been canceled successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to cancel booking";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  const { mutate: confirmBooking, isPending: isConfirming } = useMutation({
    mutationFn: async (bookingId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );
      return service.apiAppointmentsBookingIdConfirmPut({
        id: bookingId,
      });
    },
    onSuccess: async () => {
      await invalidateAndRefetchAppointmentBookings(queryClient);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Appointments],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.AppointmentSchedule],
      });
      await invalidatePostedTuitionLedgerQueries(queryClient);
      toast({
        title: "Booking confirmed",
        description: "Private lesson booking has been confirmed successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to confirm booking";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  const { mutate: rejectBooking, isPending: isRejecting } = useMutation({
    mutationFn: async (bookingId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );
      return service.apiAppointmentsBookingIdRejectPut({
        id: bookingId,
      });
    },
    onSuccess: async () => {
      await invalidateAndRefetchAppointmentBookings(queryClient);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Appointments],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.AppointmentSchedule],
      });
      await invalidatePostedTuitionLedgerQueries(queryClient);
      toast({
        title: "Booking rejected",
        description: "Private lesson booking has been rejected successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to reject booking";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return {
    cancelBooking,
    confirmBooking,
    rejectBooking,
    isCanceling,
    isConfirming,
    isRejecting,
  };
};
